// ============================================================
// TMR Portal Admin — como um modelo de contrato se lê, e o que a tela avisa sobre ele
// ============================================================
import { ApiError } from '@tmr/api-client';
import type {
  ContractTemplateSummary,
  LegacyImportItem,
  LegacyImportReport,
  TemplateStatus,
  TemplateVersion,
  TemplateVersionStatus,
} from './contract';

/** How a template's status reads on screen. */
export function templateStatusLabel(status: TemplateStatus): string {
  return status === 'Active' ? 'Ativo' : 'Arquivado';
}

/** How a version's status reads on screen. */
export function versionStatusLabel(status: TemplateVersionStatus): string {
  const labels: Record<TemplateVersionStatus, string> = {
    Draft: 'Rascunho',
    Published: 'Vigente',
    Superseded: 'Substituída',
  };

  return labels[status];
}

/**
 * Who the template applies to.
 *
 * An override whose Empresa no longer resolves shows its absence rather than disappearing: a template
 * pointing at an Empresa nobody can find is precisely what somebody needs to see.
 */
export function templateScopeLabel(template: ContractTemplateSummary): string {
  if (template.companyId === null) return 'Todas as clínicas';

  return template.companyName ?? 'Empresa não encontrada';
}

/**
 * Whether the write was refused because somebody else changed the template first.
 *
 * It is the one failure the editor must not treat as an error to shrug at: nothing was saved, and the text
 * on screen is the only copy of what the operator wrote (`24` §9).
 */
export function isWriteConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * Whether this clinic's override was published **before** the global template it overrides.
 *
 * The override does not follow the global: publishing the global text does not touch a clinic that has its
 * own. That is the point of an override and also its trap — nobody is told that the text they negotiated
 * an exception to has since moved on. This is what puts "o modelo global mudou depois" on the screen.
 *
 * @param override A clinic's template.
 * @param templates Every template, as the list returns them.
 * @returns True only when both have a published version and the global's is newer.
 */
export function globalChangedAfterOverride(
  override: ContractTemplateSummary,
  templates: ContractTemplateSummary[],
): boolean {
  if (override.companyId === null || override.publishedAt === null) return false;

  const global = templates.find(
    (candidate) =>
      candidate.companyId === null
      && candidate.documentType === override.documentType
      && candidate.status === 'Active',
  );

  if (!global?.publishedAt) return false;

  return new Date(global.publishedAt).getTime() > new Date(override.publishedAt).getTime();
}

/**
 * The templates in the order the screen reads them: by document type, the global one first, then the
 * overrides by name.
 *
 * The global goes first because the overrides are exceptions **to it** — a list sorted by name alone would
 * scatter each type's baseline among the clinics that departed from it.
 */
export function sortTemplates(templates: ContractTemplateSummary[]): ContractTemplateSummary[] {
  return [...templates].sort((a, b) => {
    if (a.documentTypeName !== b.documentTypeName) {
      return a.documentTypeName.localeCompare(b.documentTypeName, 'pt-BR');
    }

    if ((a.companyId === null) !== (b.companyId === null)) return a.companyId === null ? -1 : 1;

    return a.name.localeCompare(b.name, 'pt-BR');
  });
}

/**
 * Why this Empresa cannot have an override — or null when it can.
 *
 * The issuing engine lives in the legacy and keys an override by `produtores.id`, so an Empresa that was
 * never exported has nowhere on the other side for the override to apply. The backend refuses it too; saying
 * so when the Empresa is picked spares the operator filling in the rest of the form first (`24` §5, Q5).
 *
 * @param company The Empresa as the directory returns it.
 */
export function overrideIneligibility(company: { legacyProducerId: number | null }): string | null {
  if (company.legacyProducerId !== null) return null;

  return 'Esta empresa ainda não foi exportada ao sistema legado. Os contratos são emitidos de lá, e sem o '
    + 'cadastro dela do outro lado uma exceção não teria onde valer.';
}

/**
 * The active template that already covers this type and scope — the one a new template would collide with.
 *
 * There is one active template per document type and scope (`24` §6), and the backend refuses a second. The
 * screen finds the existing one first so it can offer to open it, which is what the operator wants anyway:
 * a new text for the same slot is a new **version** of that template, not a new template.
 *
 * @param templates Every template, as the list returns them.
 * @param documentType The type being created.
 * @param companyId The Empresa of an override; null for the global template.
 */
export function findActiveTemplate(
  templates: ContractTemplateSummary[],
  documentType: string,
  companyId: string | null,
): ContractTemplateSummary | null {
  return (
    templates.find(
      (candidate) =>
        candidate.status === 'Active'
        && candidate.documentType === documentType
        && candidate.companyId === companyId,
    ) ?? null
  );
}

/**
 * Whether this document type has a global text in force — what an override is created from.
 *
 * An override is born as a copy of the global's version in force (`24` §3.2), so a type whose global was
 * never published has nothing to copy, and the backend refuses the override. A global that exists only as
 * a draft does not count: it issues nothing, and an exception to it would be an exception to nothing.
 *
 * @param templates Every template, as the list returns them.
 * @param documentType The type the override would be of.
 */
export function hasPublishedGlobal(templates: ContractTemplateSummary[], documentType: string): boolean {
  return templates.some(
    (candidate) =>
      candidate.companyId === null
      && candidate.documentType === documentType
      && candidate.status === 'Active'
      && candidate.currentVersionNumber !== null,
  );
}

/**
 * What the row says about a template's state, in one phrase.
 *
 * "Nunca publicado" is the one worth spelling out: the template exists and looks fine, and it issues
 * nothing — the same gap the screen warns about at the top, seen from the row.
 */
export function templateStateSummary(template: ContractTemplateSummary): string {
  if (template.status === 'Archived') return 'Arquivado';
  if (template.currentVersionNumber === null) return 'Nunca publicado';
  if (template.hasDraft) return `Versão ${template.currentVersionNumber} vigente · rascunho em edição`;

  return `Versão ${template.currentVersionNumber} vigente`;
}

/**
 * The versions that were ever in force, newest first — what the history lists.
 *
 * The draft is left out: it is on the editor already, and it has not been anything a patient signed.
 *
 * @param versions Every version of the template, in any order.
 */
export function publishedHistory(versions: TemplateVersion[]): TemplateVersion[] {
  return versions
    .filter((version) => version.status !== 'Draft')
    .sort((a, b) => b.number - a.number);
}

/**
 * The version that was in force before this one — what "o que mudou" compares it with.
 *
 * Null for the first publication, which changed nothing because nothing came before it.
 *
 * @param versions Every version of the template.
 * @param version The version being looked at.
 */
export function previousPublished(
  versions: TemplateVersion[],
  version: TemplateVersion,
): TemplateVersion | null {
  return publishedHistory(versions).find((candidate) => candidate.number < version.number) ?? null;
}

/**
 * A hash cut to what a person compares by eye, with the ellipsis saying it was cut.
 *
 * @param hash A SHA-256 in hex; null on a version that has none.
 */
export function shortHash(hash: string | null): string {
  if (!hash) return '—';

  return `${hash.slice(0, 16)}…`;
}

/**
 * The v1.1 templates that are not here yet — waiting to be imported, or refused with a reason.
 *
 * @param report The initial load's preview; undefined while it has not answered or where there is no legacy.
 */
export function legacyTemplatesMissingHere(report: LegacyImportReport | undefined): LegacyImportItem[] {
  return (report?.items ?? []).filter((item) => item.outcome === 'Pending' || item.outcome === 'NotImported');
}

/**
 * The v1.1 template that is in force for this slot and is not here — or null when there is none.
 *
 * A slot is a document type and a scope (global, or one clinic). When the v1.1 issues from a template the
 * V2 has not brought yet, creating one here for the same slot produces a template the legacy refuses
 * forever, while this screen shows it as the text in force. The screen offers the import instead.
 *
 * @param report The initial load's preview.
 * @param documentType The slot's type.
 * @param legacyProducerId The clinic's legacy id for an override; null for the global.
 */
export function legacySlotHolder(
  report: LegacyImportReport | undefined,
  documentType: string,
  legacyProducerId: number | null,
): LegacyImportItem | null {
  return (
    legacyTemplatesMissingHere(report).find(
      (item) =>
        item.active && item.documentType === documentType && item.legacyProducerId === legacyProducerId,
    ) ?? null
  );
}

/**
 * Whether an error means "this environment has no such integration" rather than a fault.
 *
 * @param error What a request threw.
 */
export function isNotConfigured(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503;
}

/**
 * Where a legacy template applies, in the operator's words.
 *
 * @param item The legacy template.
 */
export function legacyScopeLabel(item: LegacyImportItem): string {
  if (item.legacyProducerId === null) return 'Todas as clínicas';

  return item.companyName ?? `Produtor ${item.legacyProducerId}`;
}
